import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useDispatch, useSelector } from 'react-redux'
// graphql
import { COMPANY_DETAILS_QUERY } from 'graphql/company/queries'
// components
import { Tab } from '@mui/material'
import { LoaderWithOverlay, Tabs } from 'components/UI'
import { AccountDetailsForm, AccountLicensingForm, SecurityForm, PrivacyAndConsentForm } from './Forms'
// constants
import { Forms } from 'constants/index'
// types
import { ICompany } from 'types/graphql'
// views
import { TabWrapper, FormWrapper } from 'themes/views/settings'
// hooks
import { useQuery } from '@apollo/client/react'
import { useCurrentUser } from 'hooks'
// store
import { setAccumulatedFormData, resetAccumulatedFormData } from 'store/reducers/accumulatedForms'
// utils
import get from 'lodash/get'
import { parseFromApi, prepareForAPI } from './utils'

const Account: React.FC = () => {
  const user = useCurrentUser()
  const router = useRouter()
  const dispatch = useDispatch()

  const { data: companyQuery, loading: isLoading } = useQuery(COMPANY_DETAILS_QUERY, {
    skip: !router.query.id,
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        id: router.query.id
      }
    }
  })

  const { accountForm } = useSelector((state: any) => ({
    accountForm: state.accumulatedForms[Forms.ACCUMULATED_CREATE_ACCOUNT]
  }))

  const [tab, setTab] = React.useState(0)

  const company: ICompany = get(companyQuery, 'companies.data[0]')

  const getInitialValues = () => {
    let initialValues = {}

    if (company) {
      initialValues = parseFromApi(company)
    }

    return initialValues
  }

  const prepareForApi = (data = {}) => prepareForAPI({ ...accountForm, ...data }, user.isAdmin)

  const handleCancelClick = () => {
    if (!user.isAdmin) {
      return router.push('/settings/users')
    }

    router.push('/settings/accounts')
  }

  const initialValues = getInitialValues()

  React.useEffect(() => {
    dispatch(resetAccumulatedFormData({ type: Forms.ACCUMULATED_CREATE_ACCOUNT }))
  }, [])

  React.useEffect(() => {
    if (company) {
      dispatch(
        setAccumulatedFormData({
          type: Forms.ACCUMULATED_CREATE_ACCOUNT,
          data: parseFromApi(company)
        })
      )
    }
  }, [company])

  return (
    <FormWrapper>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        indicatorColor='primary'
        textColor='primary'
        variant={null}>
        <Tab label='Account Details' />
        {user.isAdmin && <Tab label='Licensing' />}
        {user.isAdmin && <Tab label='Security' />}
        <Tab label='Privacy & Consent' />
      </Tabs>
      <LoaderWithOverlay isLoading={isLoading} />
      {!isLoading && (
        <TabWrapper container direction={'column'} justifyContent={'space-between'}>
          {tab === 0 && (
            <AccountDetailsForm
              companyId={company?.id}
              prepareForApi={prepareForApi}
              goNextTab={() => setTab(1)}
              initialValues={initialValues}
              handleCancelClick={handleCancelClick}
            />
          )}
          {tab === 1 && user.isAdmin && (
            <AccountLicensingForm
              accountForm={accountForm}
              goNextTab={() => setTab(2)}
              prepareForApi={prepareForApi}
              handleCancelClick={handleCancelClick}
            />
          )}
          {tab === 2 && user.isAdmin && (
            <SecurityForm
              goNextTab={() => setTab(3)}
              initialValues={initialValues}
              prepareForApi={prepareForApi}
              handleCancelClick={handleCancelClick}
            />
          )}
          {tab === (user.isAdmin ? 3 : 1) && (
            <PrivacyAndConsentForm
              accountForm={accountForm}
              prepareForApi={prepareForApi}
              handleCancelClick={handleCancelClick}
            />
          )}
        </TabWrapper>
      )}
    </FormWrapper>
  )
}

export default Account
